import type { ObligationResponse } from '@gamble-chess/contracts';
import type { JSX, ReactNode } from 'react';

import { useLedger } from '../api/stakes.ts';
import { describeDebt, formatDateTime, platformLabels, resultLabels } from '../format.ts';
import { Card } from '../ui/Card.tsx';
import { Entry, EntryList } from '../ui/EntryList.tsx';
import { Loaded } from '../ui/Loaded.tsx';
import { hintClass, linkClass } from '../ui/styles.ts';

function ChargeEntry({ obligation }: { readonly obligation: ObligationResponse }): JSX.Element {
  const { game } = obligation;

  return (
    <Entry
      lead={`${resultLabels[game.result]}: ${game.player} vs ${game.opponent}`}
      meta={
        <>
          <span className={hintClass}>
            {obligation.source.label} ({obligation.source.kind === 'rule' ? 'rule' : 'wager'})
          </span>
          <span className={hintClass}>
            <a href={game.url} className={linkClass} rel="noreferrer">
              {formatDateTime(game.playedAt)}
            </a>
            {' · '}
            <span className="tabular-nums">
              {game.playerRating} vs {game.opponentRating}
            </span>
            {` · ${platformLabels[game.platform]}`}
          </span>
        </>
      }
      trailing={
        <span
          className={`font-medium whitespace-nowrap ${obligation.amount > 0 ? 'text-debt' : 'text-credit'}`}
        >
          {describeDebt(obligation.counterparty, obligation.amount, obligation.unit)}
        </span>
      }
    />
  );
}

export interface ChargesProps {
  readonly title: string;
  /** How many of the newest charges to show; all of them when left out. */
  readonly limit?: number;
  readonly actions?: ReactNode;
}

/** What put the balances where they are, newest first. */
export function Charges({ title, limit, actions }: ChargesProps): JSX.Element {
  const ledger = useLedger();

  return (
    <Card title={title} actions={actions}>
      <Loaded
        query={ledger}
        loading="Loading your charges…"
        error="Your charges could not be loaded. Please reload the page."
      >
        {({ obligations }) =>
          obligations.length === 0 ? (
            <p className={hintClass}>
              Nothing charged yet. Losses under a standing rule and settled wagers show up here.
            </p>
          ) : (
            <EntryList label="Charges">
              {(limit === undefined ? obligations : obligations.slice(0, limit)).map(
                (obligation) => (
                  <ChargeEntry key={obligation.id} obligation={obligation} />
                ),
              )}
            </EntryList>
          )
        }
      </Loaded>
    </Card>
  );
}
